import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type { LeaguePageHeader_cardCollection } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--quadruple-unit) var(--double-unit);
`;
const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
`;

const CompetitionLogo = styled.img`
  height: 64px;
  object-fit: contain;
`;
type Props = {
  cardCollections: Nullable<LeaguePageHeader_cardCollection[]>;
};

export const LeaguePageHeader = ({ cardCollections }: Props) => {
  const cardCollection = cardCollections?.at(0);
  const { anyTeam } = cardCollection || {};
  const fulfilledSlotsCount =
    cardCollections?.reduce((acc, cur) => {
      return acc + (cur.userCardCollection?.fulfilledSlotsCount || 0);
    }, 0) || 0;
  const slotsCount =
    cardCollections?.reduce((acc, cur) => {
      return acc + (cur.userCardCollection?.slotsCount || 0);
    }, 0) || 1;
  const competition = isType(anyTeam, 'Club') ? anyTeam.domesticLeague : null;
  return (
    <Root center gap={3}>
      {!!competition?.pictureUrl && (
        <CompetitionLogo
          src={competition?.pictureUrl}
          alt={competition?.displayName}
        />
      )}
      <SBHorizontal gap={4}>
        <Vertical center>
          <LabelS color="var(--c-nd-500)">
            <FormattedMessage
              id="LeaguePageHeader.completion"
              defaultMessage="Completion"
            />
          </LabelS>
          <Horizontal>
            <StyledCircularProgress
              progress={fulfilledSlotsCount}
              maxProgress={slotsCount}
              style={
                {
                  '--circular-progress-stroke':
                    COMPETITION_COLORS[competition?.slug || ''],
                } as CSSProperties
              }
            />
            <LabelL bold>
              <FormattedNumber
                value={fulfilledSlotsCount / slotsCount}
                maximumFractionDigits={0}
                style="percent"
              />
            </LabelL>
          </Horizontal>
        </Vertical>
        <Vertical center>
          <LabelS color="var(--c-nd-500)">
            <FormattedMessage {...glossary.cards} />
          </LabelS>
          <Horizontal gap={0.5}>
            <LabelL bold>{fulfilledSlotsCount}</LabelL>/
            <LabelL bold color="var(--c-nd-500)">
              {slotsCount}
            </LabelL>
          </Horizontal>
        </Vertical>
      </SBHorizontal>
    </Root>
  );
};

LeaguePageHeader.fragments = {
  cardCollection: gql`
    fragment LeaguePageHeader_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        ... on Club {
          slug
          domesticLeague {
            slug
            pictureUrl
            displayName
          }
        }
      }
      userCardCollection(forUserSlug: $userSlug) {
        id
        slotsCount
        fulfilledSlotsCount
      }
    }
  ` as TypedDocumentNode<LeaguePageHeader_cardCollection>,
};
